import {
  BarsOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  FieldBinaryOutlined,
  FieldNumberOutlined,
  FieldStringOutlined,
  FileOutlined,
  FlagOutlined,
  PartitionOutlined,
  PictureOutlined,
  TagOutlined,
} from "@ant-design/icons";
import * as React from "react";

import { ZAttrMeta } from "../ZAttrMeta";
import { AttrUserType } from "./AttrUserType";

const emptyAttr: Omit<ZAttrMeta, "type"> = {
  name: "",
  title: "",
  uid: "",
  displayId: 0,
};

/**
 * Список таких шаблонов утверждён тут:
 * http://wiki.d4r.int/spaces/MDM20/pages/514856982/%D0%90%D1%82%D1%80%D0%B8%D0%B1%D1%83%D1%82%D1%8B#id-%D0%90%D1%82%D1%80%D0%B8%D0%B1%D1%83%D1%82%D1%8B-%D0%90%D1%82%D1%80%D0%B8%D0%B1%D1%83%D1%82%D1%8B%D0%90%D1%82%D1%80%D0%B8%D0%B1%D1%83%D1%82%D1%8B
 */
export const attrUserTypes: AttrUserType[] = [
  {
    key: "list",
    name: "Вложенная группа атрибутов",
    icon: <PartitionOutlined />,
    match: ({ type }) => type.name === "list",
    create: () => ({
      ...emptyAttr,
      type: { name: "list" },
    }),
  },
  {
    key: "float",
    name: "Дробное число",
    icon: <FieldBinaryOutlined />,
    match: ({ type }) => type.name === "number" && type.precision !== 0,
    create: () => ({
      ...emptyAttr,
      type: {
        name: "number",
        precision: 2,
      },
    }),
  },
  {
    key: "int",
    name: "Целое число",
    icon: <FieldNumberOutlined />,
    match: ({ type }) => type.name === "number" && type.precision === 0,
    create: () => ({
      ...emptyAttr,
      type: {
        name: "number",
        precision: 0,
      },
    }),
  },
  {
    key: "string",
    name: "Текст",
    icon: <FieldStringOutlined />,
    match: ({ type }) => type.name === "string",
    create: () => ({
      ...emptyAttr,
      type: { name: "string" },
    }),
  },

  {
    key: "selectList",
    name: "Текст из списка",
    icon: <BarsOutlined />,
    match: ({ type, edit }) =>
      type.name === "strOption" &&
      edit?.component?.type === "Select" &&
      edit.component.options.type === "inline",
    create: () => ({
      ...emptyAttr,
      type: { name: "strOption" },
      edit: {
        component: {
          type: "Select",
          options: {
            type: "inline",
            list: [],
          },
          search: "part",
        },
      },
    }),
  },

  {
    key: "linkToRecord",
    name: "Ссылка на запись",
    icon: <EnvironmentOutlined />,
    match: ({ type }) => type.name === "strOption" && type.role === "record",
    create: () => ({
      ...emptyAttr,
      type: {
        name: "strOption",
        role: "record",
      },
      edit: {
        component: {
          type: "RecordSelect",
          catalogId: "",
        },
      },
    }),
  },

  {
    key: "files",
    name: "Файл",
    icon: <FileOutlined />,
    match: ({ type }) =>
      type.name === "array" &&
      type.item?.type.name === "file" &&
      !type.item.type.image,
    create: () => ({
      ...emptyAttr,
      type: {
        name: "array",
        item: {
          type: { name: "file" },
        },
      },
      view: {
        component: { type: "Files" },
      },
      edit: {
        component: {
          type: "Uploader",
          isImage: false,
        },
        rules: [{ type: "file" }],
      },
    }),
  },

  {
    key: "images",
    name: "Изображение", // имеется в виду Внутреннее изображение
    icon: <PictureOutlined />,
    match: ({ type }) =>
      type.name === "array" &&
      type.item?.type.name === "file" &&
      !!type.item.type.image,
    create: () => ({
      ...emptyAttr,
      type: {
        name: "array",
        item: {
          type: { name: "file", image: true },
        },
      },
      view: {
        component: {
          type: "Image",
          maxWidth: 100,
          maxHeight: 100,
        },
      },
      edit: {
        component: {
          type: "Uploader",
          isImage: true,
          accept: "image/*",
        },
        rules: [{ type: "file" }],
      },
    }),
  },

  {
    key: "dateTime",
    name: "Дата и время",
    icon: <CalendarOutlined />,
    match: ({ type }) => type.name === "dateTime",
    create: () => ({
      ...emptyAttr,
      type: { name: "dateTime", dateOnly: false },
    }),
  },

  {
    key: "boolean",
    name: "Логический",
    icon: <FlagOutlined />,
    match: ({ type }) => type.name === "boolean",
    create: () => ({
      ...emptyAttr,
      type: { name: "boolean" },
    }),
  },

  {
    key: "valuesCatalog",
    name: "Выбор из каталога значений",
    icon: <TagOutlined />,
    match: ({ type, edit }) =>
      type.name === "strOption" &&
      (edit?.component?.type === "SelectValuesModal" ||
        (edit?.component?.type === "Select" &&
          edit.component.options.type === "valuesCatalog")),
    create: () => ({
      ...emptyAttr,
      type: { name: "strOption" },
      edit: {
        component: {
          type: "Select",
          options: {
            type: "valuesCatalog",
            valuesCatalogId: "", // <-- Пустые значения не должны пройти валидацию
            labelTemplate: "", // <--
          },
          search: "part",
        },
      },
    }),
  },
];
